import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const VideoEditingPage = () => {
    // List of video URLs provided by the user
    const videoUrls = [
        "https://youtu.be/c7u-M910bZQ",
        "https://youtu.be/osBjY3Wo08w",
        "https://youtu.be/y35Lslz95cI",
        "https://youtu.be/wfKBco7IFt8",
        "https://youtu.be/ixKSX4tgXNQ",
        "https://youtu.be/39ctE7Eiek8"
    ];

    const videoRefs = useRef([]);
    const [videosLoaded, setVideosLoaded] = useState([]);

    useEffect(() => {
        // Initialize the videosLoaded state with false for each video
        setVideosLoaded(new Array(videoUrls.length).fill(false));

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const index = parseInt(entry.target.dataset.index, 10);
                        setVideosLoaded(prev => {
                            const newLoaded = [...prev];
                            newLoaded[index] = true;
                            return newLoaded;
                        });
                        // Stop observing the video once it's loaded
                        observer.unobserve(entry.target);
                    }
                });
            },
            {
                root: null, // use the viewport
                rootMargin: '100px', // start loading when the video is 100px from view
                threshold: 0.1, // trigger when 10% of the element is visible
            }
        );

        // Attach the observer to each video container
        videoRefs.current.forEach((ref) => {
            if (ref) {
                observer.observe(ref);
            }
        });

        // Cleanup the observer on component unmount
        return () => {
            videoRefs.current.forEach((ref) => {
                if (ref) {
                    observer.unobserve(ref);
                }
            });
        };
    }, [videoUrls]);

    // Helper function to extract YouTube video ID from various URL formats
    const getVideoId = (url) => {
        let videoId = '';
        if (url.includes("youtube.com/watch?v=")) {
            videoId = url.split("v=")[1];
            const ampersandPosition = videoId.indexOf('&');
            if (ampersandPosition !== -1) {
                videoId = videoId.substring(0, ampersandPosition);
            }
        } else if (url.includes("youtu.be/")) {
            videoId = url.split("youtu.be/")[1];
            const questionMarkPosition = videoId.indexOf('?');
            if (questionMarkPosition !== -1) {
                videoId = videoId.substring(0, questionMarkPosition);
            }
        }
        return videoId;
    };

    return (
        <div className="min-h-screen bg-[#0A1128] text-[#F8F8F8] font-sans overflow-x-hidden">

            {/* Section 1: Hero/Introduction - Background with image */}
            <section
                className="relative py-20 text-center px-6 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url('https://i.imgur.com/vyiPWaQ.jpeg')` }}
            >
                <div className="absolute inset-0 bg-black bg-opacity-20 z-0"></div>
                <div className="relative z-10 container mx-auto flex flex-col items-center justify-center">
                    <div className="bg-black bg-opacity-60 p-8 rounded-lg max-w-4xl w-full">
                        <div className="w-full flex justify-start mb-8">
                            <Link
                                to="/graphics-design"
                                className="px-6 py-2 bg-[#C9B072] text-[#0A1128] font-semibold text-base rounded-full shadow-lg transition duration-300 transform hover:scale-105 hover:bg-opacity-90"
                            >
                                &larr; Back to Graphics & Design
                            </Link>
                        </div>
                        <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-[#C9B072] mb-4 sm:mb-6">
                            Video Editing & Production
                        </h1>
                        <p className="text-lg sm:text-xl md:text-2xl text-[#F8F8F8] max-w-3xl mx-auto mb-8 sm:mb-10">
                            Bringing your stories to life through dynamic video content.
                        </p>
                    </div>
                </div>
            </section>

            {/* Video Content/Examples Section - Responsive Grid */}
            <section className="py-20 bg-gray-100 border-t border-[#C9B072] text-center px-6">
                <div className="container mx-auto max-w-5xl">
                    <h2 className="text-4xl sm:text-5xl font-extrabold text-[#0A1128] mb-12">
                        Our Video Portfolio
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 w-full">
                        {videoUrls.map((url, index) => {
                            const videoId = getVideoId(url);
                            const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : 'https://placehold.co/480x360/CCC/555?text=Video+Unavailable';

                            return (
                                <div
                                    key={index}
                                    className="relative w-full overflow-hidden rounded-lg shadow-xl bg-gray-800 cursor-pointer"
                                    style={{ paddingTop: '56.25%' }}
                                    ref={(el) => (videoRefs.current[index] = el)}
                                >
                                    {/* Conditionally render iframe or thumbnail */}
                                    {videosLoaded[index] ? (
                                        <iframe
                                            className="absolute top-0 left-0 w-full h-full"
                                            src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
                                            title={`YouTube video player ${index + 1}`}
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                            referrerPolicy="strict-origin-when-cross-origin"
                                            allowFullScreen={true}
                                        ></iframe>
                                    ) : (
                                        <div
                                            className="absolute top-0 left-0 w-full h-full bg-cover bg-center rounded-lg group"
                                            style={{ backgroundImage: `url(${thumbnailUrl})` }}
                                            onClick={() => setVideosLoaded(prev => {
                                                const newLoaded = [...prev];
                                                newLoaded[index] = true;
                                                return newLoaded;
                                            })}
                                        >
                                            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 group-hover:bg-opacity-50 transition-all duration-300">
                                                {/* Play Button Icon (SVG) */}
                                                <svg className="w-16 h-16 text-white transform group-hover:scale-110 transition-transform duration-300" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M8 5v14l11-7z" />
                                                </svg>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Bottom Navigation Button */}
            <div className="flex justify-center py-10">
                <Link
                    to="/graphics-design"
                    className="px-6 sm:px-8 py-2 sm:py-3 bg-[#C9B072] text-[#0A1128] font-semibold text-base sm:text-lg rounded-full shadow-lg transition duration-300 transform hover:scale-105 hover:bg-opacity-90 inline-flex items-center justify-content-center"
                >
                    Back to Graphics & Design
                </Link>
            </div>
        </div>
    );
};

export default VideoEditingPage;
